import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline' | 'ghost' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'md', ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center rounded-lg font-medium break-keep transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          {
            // Variants - Updated with brand colors
            'bg-amber-700 text-white hover:bg-amber-800 active:bg-amber-900 focus-visible:ring-amber-700':
              variant === 'default',
            'border border-forest-600 bg-white text-forest-600 hover:bg-forest-50 active:bg-forest-100 focus-visible:ring-forest-600':
              variant === 'outline',
            'text-forest-600 hover:bg-forest-50 active:bg-forest-100 focus-visible:ring-forest-600':
              variant === 'ghost',
            'bg-error text-white hover:bg-error-dark focus-visible:ring-error':
              variant === 'destructive',

            // Sizes
            'px-3 py-1.5 text-sm': size === 'sm',
            'px-4 py-2 text-base': size === 'md',
            'px-6 py-3 text-lg': size === 'lg',
          },
          className
        )}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';
